import { computed, MaybeRefOrGetter, watch } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";

import { ProgressionType } from "@/helpers/types";
import {
  PerContentSettings,
  standardizePublicationStorageKeys,
} from "@/helpers/settings";
import { useWpsStore } from "./wps";

import {
  encodeBase64Unicode,
  toBase64Url,
} from "../../modules/shared-utils/base64";

export type StoredPublication = {
  key?: string;
  progression?: ProgressionType;
  version?: number;
  settings?: PerContentSettings;
};

// vueuse's `useLocalStorage` coerces a undefined key to the literal
// string "undefined" (or "null") when reading or writing localStorage.
// We may have past deploy that did write at that key causing initialLocator not found.
try {
  localStorage.removeItem("undefined");
  localStorage.removeItem("null");
} catch {
  // empty catch because cleanup is best-effort and silently ignoring failures is fine
}

export const usePublicationStore = defineStore("publication", () => {
  const wps = useWpsStore();

  const key: MaybeRefOrGetter<string | undefined> = () => {
    const id = wps.client?.identifier;
    if (!id) {
      return undefined;
    }

    const hashCode = encodeBase64Unicode(id);
    const safeHashCode = toBase64Url(hashCode);

    return `DM__publications__${safeHashCode}`;
  };

  const storage = useLocalStorage<StoredPublication>(
    key,
    { version: 100, settings: {} },
    {
      // setting the key dynamically through the `mergeDefaults` callback
      // this is the only way to know if the computed storage key match the current storage data
      mergeDefaults: (value, defaults) => ({
        ...defaults,
        ...value,
        key: key(),
      }),
      flush: "post",
      writeDefaults: false,
    }
  );

  /**
   * Because the storage key is dynamic, we can't run the migration once at initialization time.
   * Instead, we watch the storage and run the migration whenever the storage is updated.
   */
  watch(
    () => storage.value,
    (storage) => {
      if (!storage || !key()) return;

      standardizePublicationStorageKeys(storage);
    },
    {
      // run at initialization time
      immediate: true,
      // run as soon as the storage is updated
      flush: "sync",
    }
  );

  const ready = computed(() => {
    const k = key();
    // check if storage data is up-to-date with the current storage key
    return typeof k !== "undefined" && k === storage.value.key;
  });

  return { storage, ready };
});
